import cn from 'classnames';
import qs from 'query-string';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useForm from 'shared/hooks/useForm';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_LANGUAGE_ID, LOOKING_FOR_JOB } from 'shared/constants';
import { isEmailValid, isStringValid } from 'shared/lib/validators';
import mapEntitiesToOptions from 'shared/lib/mapEntitiesToOptions';
import Alert from 'shared/components/Alert';
import BackButton from 'shared/components/buttons/Back';
import Divider from 'shared/components/Divider';
import Card from 'shared/components/Card';
import Tip from 'shared/components/Tip';
import Subheader from 'shared/components/Subheader';
import TranslateError from 'shared/components/TranslateError';
import { Input, Submit, Select, Checkbox, Radio } from 'shared/components/Form';
import Page from 'userportal/components/Page';
import Oauth from 'userportal/components/Oauth';
import apiClient from 'userportal/lib/ApiClient';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import sklik from 'userportal/lib/sklik';
import { selectors as fieldSelectors } from 'userportal/ducks/fields';
import { selectors as langSelectors } from 'userportal/ducks/languages';
import { userportal as routes, backoffice, createBackofficeLink } from 'shared/routes';
import styles from './Signup.module.scss';

export const Signup = () => {
  const [isUser, setIsUser] = useState(false);
  const { as: signupAs } = qs.parse(location.search);
  const fields = mapEntitiesToOptions(useSelector(fieldSelectors.getAllFields), 'id', 'slug');
  const languages = mapEntitiesToOptions(useSelector(langSelectors.geti18nLanguages), 'code', 'slug');
  const allLanguages = mapEntitiesToOptions(useSelector(langSelectors.getAllLanguages), 'id', 'slug');

  const [signupState, setState] = useState({ error: null, result: null, processing: false });
  const { i18n, t } = useTranslation('userportal');

  const fieldsList = fields.map(field => ({ value: field.value, label: t(`common:field.${field.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const languagesList = languages
    .map(language => ({ value: language.value, label: t(`common:language.${language.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const allLanguagesList = allLanguages
    .map(language => ({ value: language.value, label: t(`common:language.${language.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const signup = async (data) => {
    const referrer = localStorage.getItem('referrer');
    setState({ processing: true, error: null, result: null });
    try {
      const result = await apiClient.post('/auth/signup', {
        email: data.email,
        firstname: data.firstname,
        lastname: data.lastname,
        note: data.note,
        data: {
          primaryFieldId: data.primaryFieldId,
          language: data.language,
          nativeLanguage: data.nativeLanguage,
          lookingForJob: data.lookingForJob,
          referrer
        }
      });
      setState(prevState => ({ ...prevState, processing: false, result }));
      googleAnalytics.event('user_signup');
      sklik.reportConversion();
      localStorage.removeItem('referrer');
    } catch (err) {
      setState(prevState => ({ ...prevState, processing: false, error: err.error }));
      throw err; // pass err to useForm
    }
  };

  useEffect(() => {
    if (signupAs === 'user') {
      setIsUser(true);
    }
  }, [signupAs]);

  const validate = (data) => {
    const errors = {};

    if (!isStringValid(data.firstname)) {
      errors.firstname = t('common:validation.fillFirstname');
    }

    if (!isStringValid(data.lastname)) {
      errors.lastname = t('common:validation.fillLastname');
    }

    if (!isEmailValid(data.email)) {
      errors.email = t('common:validation.fillValidEmail');
    }

    if (!data.agreement) {
      errors.agreement = t('common:validation.notCheckedAgreement');
    }

    return errors;
  };

  const { values, errors, hasError, handleChange, handleSubmit, setValue } = useForm(signup, {
    firstname: '',
    lastname: '',
    email: '',
    note: '',
    primaryFieldId: 1,
    agreement: false,
    lookingForJob: LOOKING_FOR_JOB.MAYBE,
    language: languages.some(l => l.value === i18n.language) ? i18n.language : DEFAULT_LANGUAGE_CODE,
    nativeLanguage: DEFAULT_LANGUAGE_ID
  }, validate);

  useEffect(() => {
    setValue('language', i18n.language);
  }, [i18n.language]);

  const handleUserClick = (event) => {
    event.preventDefault();
    setIsUser(true);
  };

  const handleCompanyClick = () => {
    window.location.href = createBackofficeLink(backoffice.signup);
  };

  return (
    <Page
      centered
      subheader={(
        <Subheader center title={t('page.signup.title')}>
          <span>{t('page.signup.subtitle')}</span>
        </Subheader>
      )}
      helmetTitle={t('page.signup.title')}
    >
      <div className="col-xl-8 col-lg-8 col-md-8 col-sm-12 col-xs-12">
        {signupState.error && <Alert variant="error"><TranslateError>{signupState.error}</TranslateError></Alert>}
        {signupState.result !== null && (
          <Tip className={styles.tip}>
            <p>
              <strong>{signupState.result.approved ? t('page.signup.invitationSent') : t('page.signup.signupRequestSent')}</strong>
            </p>
            <p>
              {signupState.result.approved ? t('page.signup.invitationSentTip') : t('page.signup.signupRequestSentTip')}
            </p>
          </Tip>
        )}
        {signupState.result === null && (
          <>
            {isUser ? (
              (
                <>
                  <BackButton onClick={() => setIsUser(false)}>
                    {t('page.signup.backLink')}
                  </BackButton>
                  <form className={styles.signup} onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <Input error={errors.firstname} iconClass="la la-user" name="firstname" value={values.firstname} onChange={handleChange} label={t('common:form.firstname')} />
                      </div>
                      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <Input error={errors.lastname} iconClass="la la-user" name="lastname" value={values.lastname} onChange={handleChange} label={t('common:form.lastname')} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <Input error={errors.email} iconClass="la la-at" name="email" value={values.email} type="email" onChange={handleChange} label={t('common:form.email')} />
                      </div>
                      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <Select
                          fullWidth
                          label={t('common:userProfile.rolesSkills.primaryField')}
                          name="primaryFieldId"
                          options={fieldsList}
                          value={values.primaryFieldId}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <Select
                          fullWidth
                          label={t('common:form.nativeLanguage')}
                          name="nativeLanguage"
                          options={allLanguagesList}
                          value={values.nativeLanguage}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                        <Select
                          fullWidth
                          label={t('common:form.language')}
                          name="language"
                          options={languagesList}
                          value={values.language}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="row mb8">
                      <div className="col-12">
                        <Input iconClass="la la-sticky-note" multiline name="note" value={values.note} onChange={handleChange} label={t('common:form.note')} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <h4>{t('common:userProfile.preferences.lookingForJobLabel')}</h4>
                      </div>
                    </div>
                    <div className="row mb16">
                      <div className="col-12">
                        <Radio
                          name="lookingForJob"
                          value={2}
                          label={t('common:lookingForJob.2')}
                          onChange={handleChange}
                          checked={values.lookingForJob === 2}
                        />
                        <Radio
                          name="lookingForJob"
                          value={1}
                          label={t('common:lookingForJob.1')}
                          onChange={handleChange}
                          checked={values.lookingForJob === 1}
                        />
                        <Radio
                          name="lookingForJob"
                          value={0}
                          label={t('common:lookingForJob.0')}
                          onChange={handleChange}
                          checked={values.lookingForJob === 0}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <Checkbox
                          name="agreement"
                          error={errors.agreement}
                          value={values.agreement}
                          onChange={handleChange}
                          label={t('page.signup.agreement')}
                          fullWidth
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 mb16">
                        <p className={styles.agreement}>
                          {t('page.signup.agreementLinks')}
                          {' '}
                          <a href={routes.tos} target="_blank" rel="noopener noreferrer">{t('footer.tos')}</a>
                          {', '}
                          <a href={routes.gdpr} target="_blank" rel="noopener noreferrer">{t('footer.gdpr')}</a>.
                        </p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 centerXs">
                        <Submit title={t('common:button.send')} disabled={hasError} onClick={handleSubmit} processing={signupState.processing} />
                      </div>
                    </div>
                  </form>
                  <Divider spacing="small">{t('page.login.or')}</Divider>
                  <Oauth />
                  <p className={styles.login}>
                    <Link to={routes.login}>{t('page.signup.login')}</Link>
                  </p>
                </>
              )
            ) : (
              <>
                <h2 className="text-center mt32 mb32">
                  {t('page.signup.chooseTitle')}
                </h2>
                <div className={cn('row', styles.topCategories)}>
                  <div className="col-lg-6 col-md-12">
                    <Card noBorder className={styles.category} onClick={handleUserClick}>
                      <i className="la la-user-tie" />
                      <div>
                        <h3>
                          <Link to="./" onClick={handleUserClick} className={styles.user}>
                            {t('page.signup.userTitle')}
                          </Link>
                        </h3>
                        <p>
                          {t('page.signup.userText')}
                        </p>
                      </div>
                    </Card>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <Card noBorder className={styles.category} onClick={handleCompanyClick}>
                      <i className="la la-building" />
                      <div>
                        <h3>
                          <a href={createBackofficeLink(backoffice.signup)} className={styles.client}>
                            {t('page.signup.companyTitle')}
                          </a>
                        </h3>
                        <p>
                          {t('page.signup.companyText')}
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Page>
  );
};

export default Signup;
